import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { 
  MessageSquare, 
  FileText, 
  Clock, 
  Shield, 
  Calendar, 
  BarChart3,
  CheckCircle,
  Users,
  Stethoscope,
  Building2,
  Factory
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { useGoogleAnalytics } from '../hooks/useGoogleAnalytics'

const problemCards = [
  {
    icon: FileText,
    title: 'Informações Perdidas',
    description:
      'Documentos espalhados, comunicação fragmentada e a constante preocupação de que algo importante foi esquecido.',
    styles: {
      container: 'border-red-200 bg-red-50',
      icon: 'bg-red-100 text-red-600'
    }
  },
  {
    icon: Clock,
    title: 'Retrabalho Constante',
    description:
      'Refazer autorizações, confirmar agendamentos múltiplas vezes e gastar horas procurando informações básicas.',
    styles: {
      container: 'border-amber-200 bg-amber-50',
      icon: 'bg-amber-100 text-amber-600'
    }
  },
  {
    icon: Users,
    title: 'Estresse da Equipe',
    description:
      'Sobrecarga de trabalho, interrupções constantes e a frustração de não conseguir focar no que realmente importa.',
    styles: {
      container: 'border-blue-200 bg-blue-50',
      icon: 'bg-blue-100 text-blue-600'
    }
  }
]

const solutionHighlights = [
  {
    title: 'Conexão consultório-hospital',
    description:
      'Transforme a indicação cirúrgica em um fluxo contínuo, do prontuário ao centro cirúrgico, sem rupturas.'
  },
  {
    title: 'Autorização e documentação integradas',
    description:
      'Consolide guias, laudos e pedidos de materiais e encaminhe para hospitais e convênios em um só movimento.'
  },
  {
    title: 'Agendamento sistemático',
    description:
      'Coordene agendas de equipes, salas e fornecedores com visibilidade total e alertas proativos.'
  },
  {
    title: 'Status em tempo real para o médico',
    description:
      'Mantenha o cirurgião informado automaticamente sobre autorizações e datas, sem depender de follow-ups manuais.'
  }
]

const audienceCards = [
  {
    icon: Building2,
    title: 'Para Clínicas Médicas',
    description: 'Padronize a jornada cirúrgica desde o consultório, com processos claros e comunicados em tempo real.',
    to: '/sou-clinica-medica',
    theme: 'border-blue-200 bg-blue-50',
    iconTheme: 'bg-blue-100 text-blue-600',
    buttonClass: 'bg-blue-600 hover:bg-blue-700',
    cta: 'Descobrir Soluções'
  },
  {
    icon: Users,
    title: 'Para Secretariados',
    description: 'Automatize solicitações e autorizações, concentre documentos e reduza gargalos operacionais.',
    to: '/sou-secretariao',
    theme: 'border-emerald-200 bg-emerald-50',
    iconTheme: 'bg-emerald-100 text-emerald-600',
    buttonClass: 'bg-emerald-600 hover:bg-emerald-700',
    cta: 'Ver Como Funciona'
  },
  {
    icon: Stethoscope,
    title: 'Para Cirurgiões',
    description: 'Acompanhe autorizações e agendas sem fricção, mantendo foco na prática clínica.',
    to: '/sou-cirurgião',
    theme: 'border-orange-200 bg-orange-50',
    iconTheme: 'bg-orange-100 text-orange-600',
    buttonClass: 'bg-orange-600 hover:bg-orange-700',
    cta: 'Conhecer Benefícios'
  }
]

const featureCards = [
  {
    icon: MessageSquare,
    title: 'Comunicação Assíncrona Inteligente',
    description:
      'Conecte hospitais, fornecedores e equipe médica com histórico centralizado e notificações direcionadas.',
    color: 'bg-blue-100 text-blue-600'
  },
  {
    icon: FileText,
    title: 'Central de Documentos',
    description:
      'Organize guias, laudos e pedidos de materiais com versionamento e permissões configuráveis.',
    color: 'bg-emerald-100 text-emerald-600'
  },
  {
    icon: Clock,
    title: 'Status em Tempo Real',
    description:
      'Mantenha cirurgiões e gestores informados sem interrupções com alertas inteligentes.',
    color: 'bg-orange-100 text-orange-600'
  },
  {
    icon: Shield,
    title: 'Gestão de Convênios',
    description:
      'Simplifique autorizações com acompanhamento individual e regras automatizadas.',
    color: 'bg-purple-100 text-purple-600'
  },
  {
    icon: Calendar,
    title: 'Agenda Inteligente',
    description:
      'Sincronize agendas, bloqueios e recursos críticos com visualização multidisciplinar.',
    color: 'bg-indigo-100 text-indigo-600'
  },
  {
    icon: BarChart3,
    title: 'Relatórios e Métricas',
    description:
      'Monitore produtividade e gargalos com relatórios personalizáveis para decisões rápidas.',
    color: 'bg-rose-100 text-rose-600'
  }
]

const hospitalHighlights = [
  {
    title: 'Linha do tempo cirúrgica unificada',
    description:
      'Receba indicações diretamente do consultório com documentação completa e status sincronizados.'
  },
  {
    title: 'Governança e conformidade',
    description:
      'Acompanhe KPIs assistenciais e garanta aderência às auditorias com trilhas automáticas.'
  },
  {
    title: 'Integração com fornecedores',
    description:
      'Coordene pedidos e entregas em um ambiente controlado, reduzindo rupturas e reagendamentos.'
  }
]

const hospitalMetrics = [
  { value: '90%', label: 'redução de ruído entre setores' },
  { value: '60%', label: 'menos tempo em reuniões de alinhamento' }
]

const supplierHighlights = [
  {
    title: 'Pedidos previsíveis',
    description:
      'Receba demandas desde a indicação médica, com guias e estimativas de uso enviados em lote.'
  },
  {
    title: 'Integração ágil',
    description:
      'Plataforma desenvolvida com flexibilidade e interoperabilidade, o que permite uma integração fluida e segura.'
  },
  {
    title: 'Portal colaborativo',
    description:
      'Compartilhe atualizaçõese documentos com histórico rastreável.'
  }
]

const supplierMetrics = [
  { value: '75%', label: 'mais previsibilidade no faturamento' },
  { value: '48h', label: 'antecedência média dos pedidos críticos' }
]

const Home = () => {
  const { trackButtonClick } = useGoogleAnalytics();

  const handleDemoClick = () => {
    trackButtonClick('marcar_demonstracao', 'home');
    window.open('https://calendly.com/cirurgiaautorizada-dev/30min', '_blank');
  };

  const handleConsultantClick = () => {
    trackButtonClick('falar_consultor', 'home');
    window.open('https://wa.me/5585982286045', '_blank');
  };

  const handleFinalDemoClick = () => {
    trackButtonClick('agendar_demonstracao_final', 'home');
    window.open('https://calendly.com/cirurgiaautorizada-dev/30min', '_blank');
  };

  const handleFinalConsultantClick = () => {
    trackButtonClick('falar_especialista_final', 'home');
    window.open('https://wa.me/5585982286045', '_blank');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-blue-900" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <div className="space-y-8">
              <Badge variant="secondary" className="w-fit rounded-full bg-white/10 px-4 py-1 text-sm font-medium text-white backdrop-blur">
                Gestão cirúrgica ponta a ponta
              </Badge>
              <div className="space-y-4">
                <h1 className="text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                  Centro de comando para seu{' '}
                  <span className="bg-gradient-to-r from-orange-400 via-yellow-300 to-rose-400 bg-clip-text text-transparent">
                    fluxo cirúrgico
                  </span>
              </h1>
                <p className="text-lg text-slate-200 md:text-xl">
                  Unificamos a decisão clínica no consultório com a execução hospitalar. Todas as etapas, do pedido inicial à sala de cirurgia, mapeadas em um único fluxo inteligente.
              </p>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row">
                <Button 
                  size="lg" 
                  className="bg-orange-500 px-8 py-4 text-lg text-white shadow-lg shadow-orange-500/30 transition hover:bg-orange-600"
                  onClick={handleDemoClick}
                >
                  Marcar Demonstração
                </Button>
                <Button
                  size="lg"
                  variant="ghost"
                  className="border border-white/50 bg-white/10 px-8 py-4 text-lg text-white shadow-lg shadow-black/10 hover:bg-white/20 hover:text-white"
                  onClick={handleConsultantClick}
                >
                  Fale com Consultor
                </Button>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-sm uppercase tracking-wide text-slate-300">Hospitais atendidos</p>
                  <p className="text-2xl font-semibold text-white">+4</p>
                  <span className="text-xs text-slate-400">Fluxos com ganho de previsibilidade consultório-hospital</span>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
                  <p className="text-sm uppercase tracking-wide text-slate-300">Tempo médio de autorização</p>
                  <p className="text-2xl font-semibold text-white">-55%</p>
                  <span className="text-xs text-slate-400">Redução comprovada com automatização ponta a ponta</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -left-8 -top-8 h-24 w-24 rounded-full bg-orange-500/20 blur-3xl" />
              <div className="absolute -right-12 bottom-12 h-32 w-32 rounded-full bg-blue-500/20 blur-3xl" />
              <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl">
                <div className="space-y-4">
                  {[
                    {
                      status: 'Autorizada',
                      color: 'bg-emerald-500',
                      date: '15/01/2024',
                      patient: 'Maria Silva',
                      detail: 'Cirurgia Cardíaca · Hospital São Lucas',
                      professional: 'Dr. Carlos Mendes'
                    },
                    {
                      status: 'Pendente',
                      color: 'bg-amber-400',
                      date: '12/01/2024',
                      patient: 'João Santos',
                      detail: 'Cirurgia Traumatológica · Hospital Santa Maria',
                      professional: 'Dra. Ana Paula Costa'
                    },
                    {
                      status: 'Agendada',
                      color: 'bg-blue-500',
                      date: '20/01/2024',
                      patient: 'Ana Costa',
                      detail: 'Cirurgia Ortopédica · Centro Médico São José',
                      professional: 'Dr. Roberto Almeida'
                    },
                    {
                      status: 'Solicitada',
                      color: 'bg-slate-300',
                      date: 'Hoje',
                      patient: 'Pedro Oliveira',
                      detail: 'Cirurgia Vascular · Hospital Universitário',
                      professional: 'Dra. Fernanda Lima'
                    }
                  ].map((item) => (
                    <div
                      key={item.patient}
                      className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-lg shadow-black/10"
                    >
                      <div className="mb-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className={`h-2.5 w-2.5 rounded-full ${item.color}`} />
                          <span className="text-sm font-semibold text-white">{item.status}</span>
                      </div>
                        <span className="text-xs text-slate-300">{item.date}</span>
                      </div>
                      <div className="space-y-1 text-slate-100">
                        <p className="text-sm font-medium">{item.patient}</p>
                        <p className="text-xs text-slate-300">{item.detail}</p>
                        <p className="text-xs text-slate-400">{item.professional}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problema Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Sua Rotina Complicada: O Caos Administrativo que Rouba Seu Tempo
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sem um conector único, a jornada cirúrgica se dispersa em sistemas, mensagens e ligações paralelas, aumentando atrasos e ruído entre consultório e hospital.
            </p>
          </div>
          
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {problemCards.map(({ icon, title, description, styles }) => {
              const Icon = icon;

              return (
                <Card
                  key={title}
                  className={`group relative overflow-hidden border-2 transition hover:-translate-y-2 hover:shadow-xl ${styles.container}`}
                >
                  <CardContent className="pt-8">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full transition duration-300 group-hover:scale-105 group-hover:shadow-lg group-hover:shadow-black/5">
                      <div className={`flex h-16 w-16 items-center justify-center rounded-full ${styles.icon}`}>
                        <Icon className="h-8 w-8" />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{title}</h3>
                    <p className="text-gray-600">{description}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Solução Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                A Solução Cirurgia Autorizada: Visibilidade Total, Controle Absoluto
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Atuamos como elo estratégico e operacional, conectando o consultório privado ao centro cirúrgico. Tudo acontece em um ambiente seguro que elimina lacunas de comunicação e centraliza decisões críticas.
              </p>
              
              <div className="space-y-6">
                {solutionHighlights.map(({ title, description }) => (
                  <div key={title} className="flex gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                    <div className="mt-1">
                      <CheckCircle className="h-6 w-6 text-emerald-500" />
                </div>
                  <div>
                      <h4 className="font-semibold text-gray-900">{title}</h4>
                      <p className="text-gray-600">{description}</p>
                </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pl-8">
              <img 
                src="/images/status-autorizacoes.png" 
                alt="Dashboard mostrando status das autorizações em tempo real" 
                className="rounded-lg shadow-lg w-full mb-4"
              />
              <p className="text-sm text-gray-500 text-center">
                Visão contínua das etapas — da indicação médica até o ato cirúrgico
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Públicos-Alvo Section */}
      <section className="py-20 bg-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Soluções Específicas Para Cada Necessidade
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Descubra como a Cirurgia Autorizada pode transformar a rotina do seu público específico.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {audienceCards.map(({ icon, title, description, to, theme, iconTheme, buttonClass, cta }) => {
              const Icon = icon;

              return (
                <Card
                  key={title}
                  className={`group flex flex-col justify-between overflow-hidden border-2 transition duration-300 hover:-translate-y-2 hover:shadow-2xl ${theme}`}
                >
                  <CardContent className="pt-8">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-white">
                      <div className={`flex h-16 w-16 items-center justify-center rounded-full ${iconTheme}`}>
                        <Icon className="h-8 w-8" />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">{title}</h3>
                    <p className="text-gray-600 mb-6">{description}</p>
                    <Button asChild className={`${buttonClass} w-full`}>
                      <Link to={to}>{cta}</Link>
                    </Button>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      </section>

      {/* Hospitais & Fornecedores */}
      <section className="relative overflow-hidden bg-slate-950 py-24 text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-950 via-slate-950 to-slate-900" />
        <div className="absolute inset-y-0 left-0 w-full max-w-2xl bg-gradient-to-r from-blue-500/10 via-transparent to-transparent blur-3xl" />
        <div className="relative mx-auto flex max-w-7xl flex-col gap-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Badge variant="outline" className="rounded-full border-white/30 bg-white/5 px-4 py-1 text-xs font-medium uppercase tracking-[0.3em] text-blue-200">
              Ecossistema conectado
            </Badge>
            <h2 className="mt-6 text-3xl font-bold md:text-4xl">
              Jornada integrada para hospitais e fornecedores
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-lg text-slate-300">
              Seja no consultório, no hospital ou junto aos fornecedores, todos percorrem a mesma linha do tempo cirúrgica, com dados compartilhados e responsabilidades claras.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <article className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-white/5 p-10 shadow-2xl backdrop-blur">
              <div className="absolute -top-28 right-10 h-40 w-40 rounded-full bg-blue-500/10 blur-3xl" />
              <div className="relative space-y-8">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/20 text-blue-200">
                    <Building2 className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-sm uppercase tracking-wider text-blue-200">Hospitais</span>
                    <h3 className="text-2xl font-semibold text-white">Governança operacional centralizada</h3>
                  </div>
                </div>
                <ul className="space-y-4">
                  {hospitalHighlights.map(({ title, description }) => (
                    <li key={title} className="flex gap-3 rounded-2xl border border-white/5 bg-white/5 p-4">
                      <CheckCircle className="mt-1 h-5 w-5 text-blue-300" />
                      <div>
                        <h4 className="font-medium text-white">{title}</h4>
                        <p className="text-sm text-slate-200">{description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-2 gap-4">
                  {hospitalMetrics.map(({ value, label }) => (
                    <div
                      key={label}
                      className="rounded-2xl border border-white/10 bg-white/10 p-4 text-left"
                    >
                      <p className="text-3xl font-semibold text-white">{value}</p>
                      <p className="text-xs uppercase tracking-wider text-slate-300">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="bg-white text-slate-900 hover:bg-slate-100"
                    onClick={handleDemoClick}
                  >
                    Solicitar demonstração
                  </Button>
                  <Button
                    size="lg"
                    variant="ghost"
                    className="border border-white/50 bg-white/10 text-white hover:bg-white/20"
                    onClick={handleConsultantClick}
                  >
                    Falar com especialista
                </Button>
                </div>
              </div>
            </article>

            <article className="relative overflow-hidden rounded-[2.5rem] border border-emerald-200/20 bg-emerald-500/10 p-10 shadow-2xl backdrop-blur">
              <div className="absolute -bottom-28 left-10 h-40 w-40 rounded-full bg-emerald-400/20 blur-3xl" />
              <div className="relative space-y-8">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-200">
                    <Factory className="h-6 w-6" />
                  </div>
                  <div>
                    <span className="text-sm uppercase tracking-wider text-emerald-200">Fornecedores</span>
                    <h3 className="text-2xl font-semibold text-white">Operações previsíveis e colaborativas</h3>
                  </div>
                </div>
                <ul className="space-y-4">
                  {supplierHighlights.map(({ title, description }) => (
                    <li key={title} className="flex gap-3 rounded-2xl border border-white/10 bg-white/10 p-4">
                      <CheckCircle className="mt-1 h-5 w-5 text-emerald-300" />
                      <div>
                        <h4 className="font-medium text-white">{title}</h4>
                        <p className="text-sm text-emerald-50">{description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="grid grid-cols-2 gap-4">
                  {supplierMetrics.map(({ value, label }) => (
                    <div
                      key={label}
                      className="rounded-2xl border border-white/10 bg-white/10 p-4 text-left"
                    >
                      <p className="text-3xl font-semibold text-white">{value}</p>
                      <p className="text-xs uppercase tracking-wider text-emerald-100">{label}</p>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <Button
                    size="lg"
                    className="bg-emerald-400 text-slate-900 hover:bg-emerald-300"
                    onClick={handleDemoClick}
                  >
                    Integrar com o hospital
                  </Button>
                  <Button
                    size="lg"
                    variant="ghost"
                    className="border border-white/50 bg-white/10 text-white hover:bg-white/20"
                    onClick={handleConsultantClick}
                  >
                    Construir SLA sob medida
                </Button>
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Funcionalidades Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Ferramentas Projetadas Para Você: Eficiência na Palma da Sua Mão
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Um conjunto robusto de funcionalidades que simplificam o dia a dia e potencializam a produtividade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featureCards.map(({ icon, title, description, color }) => {
              const Icon = icon;

              return (
                <Card
                  key={title}
                  className="group h-full border border-slate-200/70 shadow-none transition duration-300 hover:-translate-y-2 hover:border-slate-300 hover:shadow-2xl"
                >
                  <CardContent className="p-8">
                    <div className="mb-6 inline-flex rounded-xl bg-white/80 p-3 transition group-hover:scale-105 group-hover:shadow-lg">
                      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
                        <Icon className="h-6 w-6" />
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{title}</h3>
                    <p className="text-gray-600">{description}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
          
          {/* Imagem demonstrativa do calendário */}
          <div className="mt-16 text-center">
            <img 
              src="/images/cirurgias-agendadas.png" 
              alt="Calendário inteligente mostrando agendamento de cirurgias" 
              className="rounded-lg shadow-lg mx-auto max-w-4xl w-full"
            />
            <p className="text-sm text-gray-500 mt-4">
              Agenda inteligente com visão completa de todas as cirurgias agendadas
            </p>
          </div>
        </div>
      </section>

      {/* CTA Final Section */}
      <section className="py-20 bg-gradient-to-r from-orange-500 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto Para Transformar Sua Gestão Cirúrgica?
          </h2>
          <p className="text-xl mb-8 text-orange-100">
            Descubra como a Cirurgia Autorizada pode simplificar sua rotina e elevar seus resultados.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-orange-600 hover:bg-gray-200 px-8 py-4 text-lg"
              onClick={handleFinalDemoClick}
            >
              Agendar Demonstração
            </Button>
            <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-orange-600 hover:bg-gray-200 px-8 py-4 text-lg"
                  onClick={handleFinalConsultantClick}
                >
              Falar com Especialista
            </Button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home


